import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from "react";
import "../../App.css";
import SidePanel from "../../components/SidePanel/SidePanel";
import styles from "./ProgressDashboard.module.css";
import axios from "axios";
import { GlobalContext } from "../../context/GlobalContext.js";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);
ChartJS.defaults.elements.point.radius = 0;

// ────────────────────────────────────────────────────────────────────────────
// Placeholder skeleton data
// ────────────────────────────────────────────────────────────────────────────
const placeholderCounts = Array.from({ length: 5 }, (_, i) => ({
    label: `#${i + 1}`,
    totalEncounteredWords: 0,
    masteredWordTotal: 0,
    topWordsCount: 0,
}));
const placeholderPercents = Array.from({ length: 5 }, (_, i) => ({
    label: `#${i + 1}`,
    percentMastered: 0,
}));

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────
const ProgressDashboard = () => {
    // ------------- Global theme -------------
    const { preferences } = useContext(GlobalContext);
    const theme = preferences?.theme || "dark"; // dark = safe default
    const [themeLoaded, setThemeLoaded] = useState(false);

    // Ensure the correct class is on <body> **synchronously** (first render)
    if (typeof document !== "undefined" && document.body.className !== theme) {
        document.body.className = theme;
    }
    // Helper ──────────────────────────────────────────────────────────────
    // Dynamically import the theme CSS *and* wait until the <link> finishes
    // loading so custom properties are available in getComputedStyle().
    const loadThemeCss = (name) =>
        import(`../../themes/${name}.css`).then(
            () =>
                new Promise((resolve, reject) => {
                    // mini-css-extract inserts a <link rel="stylesheet"> tag
                    const link = [
                        ...document.head.querySelectorAll('link[rel="stylesheet"]'),
                    ].find((l) => l.href.includes(`${name}.css`));

                    // If the link can’t be found we resolve immediately
                    if (!link) return resolve();

                    link.addEventListener("load", resolve, { once: true });
                    link.addEventListener("error", reject, { once: true });
                })
        );
    // Load the CSS file for the current theme and wait for it to finish
    useEffect(() => {
        let cancelled = false;
        setThemeLoaded(false);

        loadThemeCss(theme)
            .then(() => {
                if (!cancelled) setThemeLoaded(true);
            })
            .catch((err) => console.error(`Failed to load ${theme} theme`, err));

        return () => {
            cancelled = true;
        };
    }, [theme]);

    // ---------------- State ----------------
    const [countsData, setCountsData] = useState(placeholderCounts);
    const [percentData, setPercentData] = useState(placeholderPercents);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const menuButtonRef = useRef(null);

    // --------------- Helpers ---------------
    const togglePanel = useCallback(() => setIsPanelOpen((prev) => !prev), []);

    // Convert a CSS custom property to rgba() – works with rgb(), rgba(), hsl(),
    // and #hex values. Falls back to white when nothing can be parsed.
    const cssVarToRgba = (varName, alpha = 1) => {
        const raw = getComputedStyle(document.body).getPropertyValue(varName).trim();

        if (!raw) return `rgba(255, 255, 255, ${alpha})`;

        // rgb() / rgba() — space- or comma-separated, optional “/” alpha
        if (raw.startsWith("rgb")) {
            const nums = raw.match(/[\d.]+/g).map(Number); // [r,g,b,(a)]
            const [r, g, b] = nums;
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }

        // #RRGGBB or #RGB
        if (raw.startsWith("#")) {
            let hex = raw.slice(1);
            if (hex.length === 3)
                hex = hex
                    .split("")
                    .map((c) => c + c)
                    .join("");
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }

        // Already a valid colour keyword / function? Return as-is.
        return /^#[\da-f]{3,6}|rgba?\(|hsl\(/i.test(raw)
            ? raw
            : `rgba(255, 255, 255, ${alpha})`;
    };

    // -------------- Effects ---------------
    // Fetch graph data once on mount
    useEffect(() => {
        const fetchGraphData = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/conversation/graph-data`
                );
                setCountsData(res.data.progressGraphData);
                setPercentData(res.data.percentGraphData);
            } catch (err) {
                console.error("Error fetching graph data:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchGraphData();
    }, []);

    // ----------- Prepare chart data ---------
    const { masteredAreaData, wordsAreaData } = useMemo(() => {
        if (!percentData.length || !countsData.length) {
            return {
                masteredAreaData: { labels: [], datasets: [] },
                wordsAreaData: { labels: [], datasets: [] },
            };
        }

        // Theme colours (dynamic)
        const primary70 = cssVarToRgba("--md-sys-color-primary", 0.7);
        const primary100 = cssVarToRgba("--md-sys-color-primary-container");

        const tertiary70 = cssVarToRgba("--md-sys-color-tertiary", 0.7);
        const tertiary100 = cssVarToRgba("--md-sys-color-tertiary-container");

        // ------------ % Mastered ------------
        const labels = percentData.map((d) => d.label);
        const masteredPct = percentData.map((d) => d.percentMastered);
        const unmasteredPct = masteredPct.map((p) => 100 - p);

        const masteredAreaData = {
            labels,
            datasets: [
                {
                    label: "Mastered %",
                    data: masteredPct,
                    stack: "combined",
                    fill: "origin",
                    tension: 0.2,
                    backgroundColor: tertiary70,
                    borderColor: tertiary100,
                    order: 1,
                },
                {
                    label: "Un-mastered %",
                    data: unmasteredPct,
                    stack: "combined",
                    fill: "origin",
                    tension: 0.2,
                    backgroundColor: primary70,
                    borderColor: primary100,
                    order: 2,
                },
            ],
        };

        // ------------ Counts chart ------------
        const countLabels = countsData.map((d) => d.label);
        const masteredWords = countsData.map((d) => d.masteredWordTotal);
        const topWords = countsData.map((d) => d.topWordsCount);

        const wordsAreaData = {
            labels: countLabels,
            datasets: [
                {
                    label: "Mastered words",
                    data: masteredWords,
                    stack: "combined",
                    fill: "origin",
                    tension: 0.1,
                    backgroundColor: tertiary70,
                    borderColor: tertiary100,
                    order: 1,
                },
                {
                    label: "Top 80 % words",
                    data: topWords,
                    stack: "combined",
                    fill: "origin",
                    tension: 0.1,
                    backgroundColor: primary70,
                    borderColor: primary100,
                    order: 2,
                },
            ],
        };

        return { masteredAreaData, wordsAreaData };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countsData, percentData, theme, themeLoaded]);

    // ------------ Chart options ------------
    const percentOptions = useMemo(() => {
        const onSurface = cssVarToRgba("--md-sys-color-on-surface");
        return {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "index", intersect: false },
            scales: {
                x: {
                    stacked: true,
                    ticks: { display: false, color: onSurface },
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    max: 100,
                    ticks: {
                        callback: (v) => `${v}%`,
                        color: onSurface,
                    },
                },
            },
            plugins: {
                title: {
                    display: true,
                    text: "% of Conversation Mastered",
                    color: onSurface,
                },
                legend: {
                    labels: { color: onSurface },
                },
                tooltip: { mode: "index", intersect: false },
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theme, themeLoaded]);

    const wordsOptions = useMemo(() => {
        const onSurface = cssVarToRgba("--md-sys-color-on-surface");
        return {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "index", intersect: false },
            scales: {
                x: {
                    stacked: true,
                    ticks: { display: false, color: onSurface },
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: { color: onSurface },
                },
            },
            plugins: {
                title: {
                    display: true,
                    text: "Top-80 vs Mastered Counts",
                    color: onSurface,
                },
                legend: {
                    labels: { color: onSurface },
                },
                tooltip: { mode: "index", intersect: false },
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theme]);

    // ---------------- Render ---------------
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="app">
            <div className="app-container">
                <header className="app-header">
                    <span style={{ marginRight: "auto" }}>
                        <md-icon-button ref={menuButtonRef} onClick={togglePanel}>
                            <md-icon>menu</md-icon>
                        </md-icon-button>
                    </span>
                </header>

                {/* Side navigation */}
                <SidePanel
                    isOpen={isPanelOpen}
                    togglePanel={togglePanel}
                    navigationItems={[
                        { label: "Home", path: "/home" },
                        { label: "Conversation", path: "/conversation" },
                        { label: "Word Bank", path: "/wordbank" },
                    ]}
                    menuButtonRef={menuButtonRef}
                />

                {loading && <div className={styles.loading}>Fetching latest data…</div>}

                <div className={styles.dashboardContainer}>
                    <div className={styles.chartWrapper}>
                        <Line
                            key={`percent-${theme}`} // forces remount on theme change
                            data={masteredAreaData}
                            options={percentOptions}
                            height={400}
                        />
                    </div>
                    <div className={styles.chartWrapper}>
                        <Line
                            key={`words-${theme}`} // forces remount on theme change
                            data={wordsAreaData}
                            options={wordsOptions}
                            height={400}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProgressDashboard;
